import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import AppSidebar from "@/components/AppSidebar";
import { toast } from "sonner";
import axios from "axios";
import { 
  Users, 
  UserPlus, 
  Mail, 
  Shield, 
  Crown, 
  User,
  Eye,
  MoreVertical,
  Clock,
  Trash2,
  ArrowUpDown,
  Loader2,
  CheckCircle2,
  XCircle,
  Zap,
  Lock
} from "lucide-react";

const ROLE_INFO = {
  owner: {
    label: "Owner",
    description: "Full access, billing, can delete business",
    color: "bg-amber-100 text-amber-800",
    icon: Crown
  },
  admin: {
    label: "Admin",
    description: "Full access except billing",
    color: "bg-purple-100 text-purple-800",
    icon: Shield
  },
  manager: {
    label: "Manager",
    description: "Manage leads and forms",
    color: "bg-blue-100 text-blue-800",
    icon: Users
  },
  member: {
    label: "Member",
    description: "View-only access",
    color: "bg-gray-100 text-gray-800",
    icon: Eye
  }
};

export default function TeamPage() {
  const { user, token, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const [owner, setOwner] = useState(null);
  const [invitations, setInvitations] = useState([]);
  const [inviting, setInviting] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("member");
  const [teamMembersEnabled, setTeamMembersEnabled] = useState(true);

  const fetchTeamData = async () => {
    try {
      setLoading(true);
      const [membersRes, invitationsRes, usageRes] = await Promise.all([
        axios.get(`${API}/team/members`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/team/invitations`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: { invitations: [] } })), // May fail if not admin
        axios.get(`${API}/business/usage`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: {} }))
      ]);
      
      setMembers(membersRes.data.members || []);
      setOwner(membersRes.data.owner);
      setInvitations(invitationsRes.data.invitations || []);
      
      // Check if plan supports team members (Professional and Enterprise do)
      const plan = usageRes.data?.subscription_plan || "starter";
      setTeamMembersEnabled(plan !== "starter");
    } catch (error) {
      console.error("Error fetching team data:", error);
      toast.error("Failed to load team data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchTeamData();
    }
  }, [token]);

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) {
      toast.error("Please enter an email address");
      return;
    }

    setInviting(true);
    try {
      await axios.post(`${API}/team/invite`, {
        email: inviteEmail,
        role: inviteRole
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Invitation sent to ${inviteEmail}`);
      setInviteEmail("");
      setInviteRole("member");
      fetchTeamData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send invitation");
    } finally {
      setInviting(false);
    }
  };

  const handleCancelInvitation = async (invitationId) => {
    try {
      await axios.delete(`${API}/team/invitations/${invitationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Invitation cancelled");
      fetchTeamData();
    } catch (error) {
      toast.error("Failed to cancel invitation");
    }
  };

  const handleChangeRole = async (memberId, newRole) => {
    try {
      await axios.put(`${API}/team/members/${memberId}/role`, {
        role: newRole
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Role updated");
      fetchTeamData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update role");
    }
  };

  const handleRemoveMember = async (memberId) => {
    try {
      await axios.delete(`${API}/team/members/${memberId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Team member removed");
      fetchTeamData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove member");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  };

  const RoleBadge = ({ role }) => {
    const info = ROLE_INFO[role] || ROLE_INFO.member;
    const Icon = info.icon;
    return (
      <Badge className={`${info.color} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {info.label}
      </Badge>
    );
  };

  // Check if current user can manage team
  const userRole = user?.email?.toLowerCase() === owner?.email?.toLowerCase() ? "owner" : 
    members.find(m => m.email?.toLowerCase() === user?.email?.toLowerCase())?.role || "member";
  const canManage = ["owner", "admin"].includes(userRole);

  return (
    <div className="min-h-screen bg-[#f5f5f0] flex" style={{ fontFamily: 'Inter, sans-serif' }}>
      <AppSidebar user={user} onLogout={logout} />
      
      <main className="flex-1 p-8 ml-64">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-[#1a4d2e]">Team</h1>
            <p className="text-[#666] mt-1">Manage your team members and their access levels</p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-[#1a4d2e]" />
            </div>
          ) : (
            <>
              {/* Upgrade Banner for Starter Plan */}
              {!teamMembersEnabled && (
                <Card className="mb-8 border-2 border-primary/30 bg-primary/5">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Lock className="w-5 h-5 text-primary" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-foreground mb-1">Team Members Available on Professional</h3>
                        <p className="text-sm text-muted-foreground mb-3">
                          Upgrade to Professional to invite team members with role-based access control. Collaborate with your team seamlessly.
                        </p>
                        <Link to="/settings">
                          <Button className="rounded-full" size="sm" data-testid="upgrade-team-btn">
                            <Zap className="w-4 h-4 mr-2" />
                            Upgrade to Professional
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Invite Section */}
              {canManage && teamMembersEnabled && (
                <Card className="mb-8 border-[#e5e5e0]">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <UserPlus className="h-5 w-5 text-[#1a4d2e]" />
                      Invite Team Member
                    </CardTitle>
                    <CardDescription>
                      Send an invitation to add someone to your team. They'll receive an email with a link to join.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleInvite} className="flex gap-4 items-end">
                      <div className="flex-1">
                        <Label htmlFor="email" className="text-sm font-medium">Email Address</Label>
                        <Input
                          id="email"
                          type="email"
                          placeholder="colleague@company.com"
                          value={inviteEmail}
                          onChange={(e) => setInviteEmail(e.target.value)}
                          className="mt-1"
                          data-testid="invite-email-input"
                        />
                      </div>
                      <div className="w-48">
                        <Label className="text-sm font-medium">Role</Label>
                        <Select value={inviteRole} onValueChange={setInviteRole}>
                          <SelectTrigger className="mt-1" data-testid="invite-role-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {userRole === "owner" && (
                              <SelectItem value="admin">
                                <div className="flex items-center gap-2">
                                  <Shield className="h-4 w-4" />
                                  Admin
                                </div>
                              </SelectItem>
                            )}
                            <SelectItem value="manager">
                              <div className="flex items-center gap-2">
                                <Users className="h-4 w-4" />
                                Manager
                              </div>
                            </SelectItem>
                            <SelectItem value="member">
                              <div className="flex items-center gap-2">
                                <Eye className="h-4 w-4" />
                                Member
                              </div>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <Button 
                        type="submit" 
                        disabled={inviting}
                        className="bg-[#1a4d2e] hover:bg-[#2d6a4f]"
                        data-testid="send-invite-btn"
                      >
                        {inviting ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <Mail className="h-4 w-4 mr-2" />
                            Send Invite
                          </>
                        )}
                      </Button>
                    </form>

                    {/* Role Descriptions */}
                    <div className="mt-6 pt-6 border-t border-[#e5e5e0]">
                      <p className="text-sm font-medium text-[#666] mb-3">Role Permissions:</p>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {Object.entries(ROLE_INFO).map(([key, info]) => (
                          <div key={key} className="text-center p-3 bg-[#f8f8f5] rounded-lg">
                            <RoleBadge role={key} />
                            <p className="text-xs text-[#666] mt-2">{info.description}</p>
                            {key === "owner" && (
                              <p className="text-xs text-[#999] mt-1 italic">(Cannot be assigned)</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Pending Invitations */}
              {canManage && invitations.length > 0 && (
                <Card className="mb-8 border-[#e5e5e0]">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Clock className="h-5 w-5 text-[#f59e0b]" />
                      Pending Invitations
                    </CardTitle>
                    <CardDescription>
                      Invitations that haven't been accepted yet. They expire after 7 days.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Email</TableHead>
                          <TableHead>Role</TableHead>
                          <TableHead>Sent</TableHead>
                          <TableHead>Expires</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {invitations.map((invite) => (
                          <TableRow key={invite.id}>
                            <TableCell className="font-medium">{invite.email}</TableCell>
                            <TableCell>
                              <RoleBadge role={invite.role} />
                            </TableCell>
                            <TableCell className="text-[#666]">{formatDate(invite.invited_at)}</TableCell>
                            <TableCell className="text-[#666]">{formatDate(invite.expires_at)}</TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleCancelInvitation(invite.id)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <XCircle className="h-4 w-4 mr-1" />
                                Cancel
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}

              {/* Team Members */}
              <Card className="border-[#e5e5e0]">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5 text-[#1a4d2e]" />
                    Team Members
                  </CardTitle>
                  <CardDescription>
                    {members.length + (owner ? 1 : 0)} member{members.length + (owner ? 1 : 0) !== 1 ? 's' : ''} in your team
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Member</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Joined</TableHead>
                        {canManage && <TableHead className="text-right">Actions</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {/* Owner */}
                      {owner && (
                        <TableRow className="bg-amber-50/50">
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center">
                                <Crown className="h-5 w-5 text-amber-600" />
                              </div>
                              <div>
                                <p className="font-medium">{owner.name || "Business Owner"}</p>
                                <p className="text-sm text-[#666]">{owner.email}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <RoleBadge role="owner" />
                          </TableCell>
                          <TableCell className="text-[#666]">{formatDate(owner.accepted_at)}</TableCell>
                          {canManage && (
                            <TableCell className="text-right text-sm text-[#999]">
                              {owner.email?.toLowerCase() === user?.email?.toLowerCase() ? "You" : "—"}
                            </TableCell>
                          )}
                        </TableRow>
                      )}

                      {/* Team Members */}
                      {members.map((member) => (
                        <TableRow key={member.id}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className="h-10 w-10 rounded-full bg-[#f0f0f0] flex items-center justify-center">
                                <User className="h-5 w-5 text-[#666]" />
                              </div>
                              <div>
                                <p className="font-medium">{member.name || "Team Member"}</p>
                                <p className="text-sm text-[#666]">{member.email}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <RoleBadge role={member.role} />
                          </TableCell>
                          <TableCell className="text-[#666]">{formatDate(member.accepted_at)}</TableCell>
                          {canManage && (
                            <TableCell className="text-right">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm">
                                    <MoreVertical className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  {/* Role change options */}
                                  {member.role !== "admin" && userRole === "owner" && (
                                    <DropdownMenuItem onClick={() => handleChangeRole(member.id, "admin")}>
                                      <Shield className="h-4 w-4 mr-2" />
                                      Make Admin
                                    </DropdownMenuItem>
                                  )}
                                  {member.role !== "manager" && (
                                    <DropdownMenuItem onClick={() => handleChangeRole(member.id, "manager")}>
                                      <Users className="h-4 w-4 mr-2" />
                                      Make Manager
                                    </DropdownMenuItem>
                                  )}
                                  {member.role !== "member" && (
                                    <DropdownMenuItem onClick={() => handleChangeRole(member.id, "member")}>
                                      <Eye className="h-4 w-4 mr-2" />
                                      Make Member
                                    </DropdownMenuItem>
                                  )}
                                  <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                      <DropdownMenuItem 
                                        className="text-red-600"
                                        onSelect={(e) => e.preventDefault()}
                                      >
                                        <Trash2 className="h-4 w-4 mr-2" />
                                        Remove
                                      </DropdownMenuItem>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent>
                                      <AlertDialogHeader>
                                        <AlertDialogTitle>Remove Team Member?</AlertDialogTitle>
                                        <AlertDialogDescription>
                                          This will remove {member.name || member.email} from your team. 
                                          They will lose access to all data.
                                        </AlertDialogDescription>
                                      </AlertDialogHeader>
                                      <AlertDialogFooter>
                                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                                        <AlertDialogAction
                                          onClick={() => handleRemoveMember(member.id)}
                                          className="bg-red-600 hover:bg-red-700"
                                        >
                                          Remove
                                        </AlertDialogAction>
                                      </AlertDialogFooter>
                                    </AlertDialogContent>
                                  </AlertDialog>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}

                      {members.length === 0 && !owner && (
                        <TableRow>
                          <TableCell colSpan={canManage ? 4 : 3} className="text-center py-12 text-[#666]">
                            <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                            <p>No team members yet</p>
                            <p className="text-sm">Invite your first team member above</p>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
